import { useCallback, useState } from "react";
import Papa from "papaparse";
import { useGetIntentTopicsByIdsLazyQuery } from "@/generated/taxonomy";
import { TAXONOMY_CLIENT } from "@/utils/consts";
import { MAX_INTENT_TOPICS, type CachedTopic } from "../slice";

/** Required CSV headers for topic upload */
const REQUIRED_HEADERS = ["topic_id"];

/** Parsed row from CSV file */
interface ParsedTopicRow {
  topic_name?: string;
  topic_id: string;
}

/** Result of CSV parsing and validation */
export interface TopicValidationResult {
  validTopics: CachedTopic[];
  invalidTopicIds: string[];
  error: string | null;
}

/** CSV parsing result */
interface CsvParseResult {
  data: ParsedTopicRow[];
  error: string | null;
}

/**
 * Parses a CSV file and extracts topic rows.
 */
const parseTopicCsv = (file: File): Promise<CsvParseResult> => {
  return new Promise((resolve) => {
    Papa.parse<ParsedTopicRow>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: (results) => {
        const headers = Object.keys(results.data[0] || {});
        const missingHeaders = REQUIRED_HEADERS.filter(
          (h) => !headers.includes(h)
        );

        if (missingHeaders.length > 0) {
          resolve({
            data: [],
            error: `Missing required column: ${missingHeaders.join(", ")}`,
          });
          return;
        }

        const validRows = results.data.filter(
          (row) => row.topic_id && row.topic_id.trim() !== ""
        );

        if (validRows.length === 0) {
          resolve({
            data: [],
            error: "No valid topic IDs found in CSV",
          });
          return;
        }

        if (validRows.length > MAX_INTENT_TOPICS) {
          resolve({
            data: [],
            error: `File exceeds limit: ${validRows.length.toLocaleString()} topics (max: ${MAX_INTENT_TOPICS.toLocaleString()})`,
          });
          return;
        }

        resolve({
          data: validRows,
          error: null,
        });
      },
      error: (error) => {
        resolve({
          data: [],
          error: `Failed to parse CSV: ${error.message}`,
        });
      },
    });
  });
};

/**
 * Formats a date as YYYY-MM-DD for filenames.
 */
const formatDateForFilename = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Hook providing bulk upload and download operations for intent topics.
 */
export const useBulkTopicOperations = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const [fetchTopics] = useGetIntentTopicsByIdsLazyQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });

  /**
   * Handles CSV file upload: parses file, validates topic IDs via API.
   */
  const handleFileUpload = useCallback(
    async (file: File): Promise<TopicValidationResult> => {
      setIsProcessing(true);

      try {
        const parseResult = await parseTopicCsv(file);

        if (parseResult.error) {
          return {
            validTopics: [],
            invalidTopicIds: [],
            error: parseResult.error,
          };
        }

        const topicIds = parseResult.data.map((row) => row.topic_id.trim());
        const uniqueTopicIds = [...new Set(topicIds)];

        const { data, error } = await fetchTopics({
          variables: { ids: uniqueTopicIds },
        });

        if (error) {
          return {
            validTopics: [],
            invalidTopicIds: [],
            error: `API validation failed: ${error.message}`,
          };
        }

        const validTopicsFromApi =
          data?.intentTopic?.data?.filter(
            (topic): topic is NonNullable<typeof topic> =>
              topic !== null && topic.id !== null && topic.displayName !== null
          ) ?? [];

        const validTopicIds = new Set(
          validTopicsFromApi.map((topic) => topic.id as string)
        );

        const validTopics: CachedTopic[] = validTopicsFromApi.map((topic) => ({
          id: topic.id as string,
          displayName: topic.displayName as string,
          category: topic.category ?? undefined,
          collectionTree: (topic.collectionTree ?? []).filter(
            (t): t is string => t !== null
          ),
        }));

        const invalidTopicIds = uniqueTopicIds.filter(
          (id) => !validTopicIds.has(id)
        );

        return {
          validTopics,
          invalidTopicIds,
          error: null,
        };
      } finally {
        setIsProcessing(false);
      }
    },
    [fetchTopics]
  );

  /**
   * Generates and downloads a CSV file from the provided topics.
   */
  const handleDownload = useCallback((topics: CachedTopic[]): void => {
    if (topics.length === 0) return;

    const csvContent = Papa.unparse({
      fields: ["topic_name", "topic_id"],
      data: topics.map((topic) => ({
        topic_name: topic.displayName,
        topic_id: topic.id,
      })),
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `intent_topics_${formatDateForFilename()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  return {
    handleFileUpload,
    handleDownload,
    isProcessing,
  };
};
